import React, { createContext, useContext, useEffect, useState } from 'react';

const AuthContext = createContext();

// Auth session lifetime — the user stays signed in for 24 hours, then must log in again.
const SESSION_MS = 24 * 60 * 60 * 1000;
const AUTH_EXPIRY_KEY = 'authExpiry';

const clearAuthStorage = () => {
  localStorage.removeItem('userId');
  localStorage.removeItem('userId1');
  localStorage.removeItem('packageDetails');
  localStorage.removeItem('price');
  localStorage.removeItem('email');
  localStorage.removeItem('userType');
  localStorage.removeItem(AUTH_EXPIRY_KEY);
  localStorage.removeItem('studyak_chat_messages');
  localStorage.removeItem('studyak_chat_id');
  localStorage.removeItem('studyak_chat_owner');
};

// True when a userId exists and the 24h window has not elapsed. When it has,
// the stale session is wiped so the Login screen's "userId present" redirect
// doesn't bounce the user back into an expired session. A legacy session with
// no stored expiry is granted a fresh 24h window so current users aren't kicked out.
const hasValidSession = () => {
  if (!localStorage.getItem('userId')) return false;
  const expiry = parseInt(localStorage.getItem(AUTH_EXPIRY_KEY), 10);
  if (Number.isNaN(expiry)) {
    localStorage.setItem(AUTH_EXPIRY_KEY, String(Date.now() + SESSION_MS));
    return true;
  }
  if (Date.now() >= expiry) {
    clearAuthStorage();
    return false;
  }
  return true;
};

export const AuthProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(() => hasValidSession());

  const signIn = () => {
    localStorage.setItem(AUTH_EXPIRY_KEY, String(Date.now() + SESSION_MS));
    setIsLoggedIn(true);
  };

  const signOut = () => {
    clearAuthStorage();
    window.dispatchEvent(new Event('studyak_chat_clear'));
    setIsLoggedIn(false);
  };

  // Auto sign-out once the 24h session elapses — covers tabs left open and
  // machines that were asleep (re-checks on window focus and a minute timer).
  useEffect(() => {
    if (!isLoggedIn) return undefined;
    const check = () => {
      if (!hasValidSession()) signOut();
    };
    const intervalId = window.setInterval(check, 60 * 1000);
    window.addEventListener('focus', check);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', check);
    };
  }, [isLoggedIn]);

  return (
    <AuthContext.Provider value={{ isLoggedIn, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
